"use client";

/**
 * Mi pedido 4.1 / 4.2 — carrito con cantidades, resumen y estado vacío.
 */

import Link from "next/link";
import { useRouter } from "next/navigation";

import { BottomNav, HeaderTitulo } from "@/components/app/chrome";
import { useApp } from "@/components/app/proveedor";
import { FotoBoot } from "@/components/app/foto-boot";
import { IconoCarrito } from "@/components/app/iconos";
import { conDescuento } from "@/components/app/precio-dist";
import {
  etiquetaPres,
  parseClave,
  precioClave,
} from "@/components/app/presentaciones";
import type { ProductoApp, ReglasEnvio, ZonaApp } from "@/components/app/tipos";

export function CarritoCliente({
  productos,
  zonas,
  reglas,
}: {
  productos: ProductoApp[];
  zonas: ZonaApp[];
  reglas: ReglasEnvio;
}) {
  const router = useRouter();
  const { carrito, setCantidad, agregar, vaciarCarrito, zonaSlug, descuentoDist } =
    useApp();

  const items = Object.entries(carrito)
    .map(([claveCarrito, qty]) => {
      const { slug, pres } = parseClave(claveCarrito);
      const p = productos.find((x) => x.slug === slug);
      if (!p) return null;
      const precioLista = precioClave(p, pres) ?? 0;
      return {
        clave: claveCarrito,
        producto: p,
        pres,
        precioLista,
        // El distribuidor ve (y paga) su precio con descuento.
        precio: conDescuento(precioLista, descuentoDist),
        qty,
      };
    })
    .filter((x): x is NonNullable<typeof x> => x !== null);

  const zona = zonas.find((z) => z.slug === zonaSlug) ?? zonas[0];
  const subtotal = items.reduce((t, i) => t + i.precio * i.qty, 0);
  /** Lo que pagaría un cliente normal (sin descuento de distribuidor). */
  const subtotalLista = items.reduce((t, i) => t + i.precioLista * i.qty, 0);
  const ahorro = subtotalLista - subtotal;
  const iva = subtotal * (reglas.ivaPorcentaje / 100);
  const envio = subtotal >= reglas.gratisDesde || subtotal === 0 ? 0 : reglas.costo;
  const total = subtotal + iva + envio;

  if (items.length === 0) {
    return (
      <>
        <HeaderTitulo titulo="Mi pedido" />
        <main className="anim-bz-in flex min-h-(--alto-app) flex-col items-center justify-center gap-3.5 px-10 pt-[100px] pb-[62px] text-center">
          <span className="flex h-[100px] w-[100px] items-center justify-center rounded-[34px] bg-humo">
            <IconoCarrito size={42} color="rgba(34,31,31,.3)" strokeWidth={1.7} />
          </span>
          <h1 className="font-app text-[26px] leading-[30px] font-black">
            Tu congelador está triste
          </h1>
          <p className="text-sm leading-[21px] text-tinta/60">
            Todavía no agregas ningún boot.
          </p>
          <Link
            href="/productos"
            className="mt-2 flex h-[58px] items-center rounded-[29px] bg-verde px-[30px] font-app text-base font-semibold text-white"
          >
            Ver boots
          </Link>
          <Link href="/pepa" className="text-[13px] text-tinta/60 underline">
            O deja que Pepa elija por ti
          </Link>
        </main>
        <BottomNav />
      </>
    );
  }

  return (
    <>
      <HeaderTitulo titulo="Mi pedido" />
      <main className="anim-bz-in min-h-(--alto-app) px-6 pt-[100px] pb-[260px]">
        <div className="flex items-center justify-between pt-5">
          <span className="text-[13px] text-tinta/50">
            {items.length} {items.length === 1 ? "boot" : "boots"} en tu pedido
          </span>
          <button
            type="button"
            onClick={vaciarCarrito}
            className="text-[13px] text-tinta/50 underline"
          >
            Vaciar carrito
          </button>
        </div>
        <div className="mt-3 flex flex-col gap-3">
          {items.map(({ clave, producto, pres, precio, qty }) => (
            <div
              key={clave}
              className="flex items-center gap-3.5 rounded-3xl border border-black/5 bg-white p-3"
            >
              <span
                className="h-[70px] w-[70px] flex-none overflow-hidden rounded-[18px]"
                style={{ background: producto.gradiente }}
              >
                <FotoBoot
                  imagen={producto.imagen}
                  gradiente={producto.gradiente}
                  alt={producto.nombre}
                  sizes="70px"
                />
              </span>
              <div className="min-w-0 flex-1">
                <div className="font-app text-base font-semibold">
                  {producto.nombre}
                </div>
                <div className="mt-0.5 text-xs text-tinta/55">
                  {etiquetaPres(pres) || "210 g"} · ${precio.toFixed(2)}
                  {pres.startsWith("mix") ? " c/u" : ""}
                </div>
                <div className="mt-2 flex h-[30px] w-[104px] items-center gap-3 rounded-[15px] bg-humo px-3">
                  <button
                    type="button"
                    aria-label="Menos"
                    onClick={() => setCantidad(clave, qty - 1)}
                    className="font-app text-base font-semibold"
                  >
                    –
                  </button>
                  <span className="flex-1 text-center font-app text-sm font-semibold">
                    {qty}
                  </span>
                  <button
                    type="button"
                    aria-label="Más"
                    onClick={() => agregar(clave)}
                    className="font-app text-base font-semibold"
                  >
                    +
                  </button>
                </div>
              </div>
              <div className="text-right">
                <div className="font-app text-base font-black">
                  ${(precio * qty).toFixed(2)}
                </div>
                <button
                  type="button"
                  onClick={() => setCantidad(clave, 0)}
                  className="mt-2.5 text-xs text-tinta/45 underline"
                >
                  Quitar
                </button>
              </div>
            </div>
          ))}
        </div>

        <Link
          href="/productos"
          className="mt-4 flex h-[52px] items-center justify-center rounded-[26px] border border-dashed border-verde/50 font-app text-[15px] font-medium text-verde"
        >
          + Agregar otro boot
        </Link>

        <div className="mt-5 rounded-[22px] bg-humo p-[18px]">
          {descuentoDist > 0 ? (
            <>
              <div className="flex justify-between text-sm">
                <span className="text-tinta/70">Subtotal precio normal</span>
                <span className="font-bold text-tinta/45 line-through">
                  ${subtotalLista.toFixed(2)}
                </span>
              </div>
              <div className="mt-2 flex justify-between text-sm">
                <span className="text-tinta/70">
                  Descuento distribuidor (−{Math.round(descuentoDist * 100)}%)
                </span>
                <span className="font-bold text-verde">−${ahorro.toFixed(2)}</span>
              </div>
              <div className="mt-2 flex justify-between text-sm">
                <span className="text-tinta/70">Subtotal distribuidor</span>
                <span className="font-bold">${subtotal.toFixed(2)}</span>
              </div>
            </>
          ) : (
            <div className="flex justify-between text-sm">
              <span className="text-tinta/70">Subtotal</span>
              <span className="font-bold">${subtotal.toFixed(2)}</span>
            </div>
          )}
          {reglas.ivaPorcentaje > 0 && (
            <div className="mt-2 flex justify-between text-sm">
              <span className="text-tinta/70">IVA ({reglas.ivaPorcentaje}%)</span>
              <span className="font-bold">${iva.toFixed(2)}</span>
            </div>
          )}
          <div className="mt-2 flex justify-between text-sm">
            <span className="text-tinta/70">Envío ({zona?.nombre ?? "tu zona"})</span>
            <span className="font-bold text-verde">
              {envio === 0 ? "Gratis" : `$${envio.toFixed(2)}`}
            </span>
          </div>
          <div className="mt-2.5 text-xs text-tinta/55">{zona?.diaLabel}</div>
        </div>
      </main>

      <div className="fixed bottom-0 left-1/2 z-30 w-full max-w-[430px] -translate-x-1/2 border-t border-black/5 bg-white/95 px-6 pt-5 pb-6">
        <div className="flex items-baseline justify-between">
          <span className="font-app text-base font-semibold">Total</span>
          <span className="font-app text-[28px] font-black">${total.toFixed(2)}</span>
        </div>
        <button
          type="button"
          onClick={() => router.push("/pedido/entrega")}
          className="mt-3.5 flex h-16 w-full items-center justify-center rounded-[30px] bg-verde font-app text-[17px] font-semibold text-white transition active:scale-[.98]"
        >
          Continuar
        </button>
        <p className="mt-2.5 text-center text-xs text-tinta/50">
          Paga por transferencia o en efectivo al recibir
        </p>
      </div>
    </>
  );
}
